import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MoreVertical, Plus, Phone, Trash2, Edit, Power, Copy } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CallFlow } from "@/model/interface/CallFlow";
import { toast } from "sonner";
import React from "react";
import {
  useDeleteCallFlowMutation,
  useDuplicateCallFlowMutation,
  useGetCallFlowsQuery,
  useToggleCallFlowMutation,
} from "@/store/rest_api";
import { ActiveFlowConflictDialog } from "./components/ActiveFlowConflictDialog";
import { apiErrorMessage } from "@/util/apiError";

export default function CallFlowsList() {
  const navigate = useNavigate();
  const { data: callFlows, isLoading, error } = useGetCallFlowsQuery();
  const [deleteCallFlow] = useDeleteCallFlowMutation();
  const [toggleCallFlow] = useToggleCallFlowMutation();
  const [duplicateCallFlow] = useDuplicateCallFlowMutation();

  const [flowToDelete, setFlowToDelete] = useState<CallFlow | null>(null);
  const [flowToActivate, setFlowToActivate] = useState<CallFlow | null>(null);

  const handleDelete = async () => {
    if (!flowToDelete) return;

    try {
      await deleteCallFlow(flowToDelete.id).unwrap();
      toast.success("Call flow deleted successfully");
      setFlowToDelete(null);
    } catch (error: any) {
      toast.error(error.detail || "Failed to delete call flow");
    }
  };

  const conflictingFlowFor = (flow: CallFlow) => {
    if (flow.active || !flow.phoneNumber?.id) {
      return undefined;
    }
    return callFlows?.find(
      (candidate) =>
        candidate.id !== flow.id && candidate.active && candidate.phoneNumber?.id === flow.phoneNumber?.id,
    );
  };

  const handleDuplicate = async (flow: CallFlow) => {
    try {
      await duplicateCallFlow(flow.id).unwrap();
      toast.success(`"${flow.name}" duplicated. The copy is turned off and not assigned to a number.`);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to duplicate call flow"));
    }
  };

  const applyToggle = async (flow: CallFlow) => {
    try {
      await toggleCallFlow(flow.id).unwrap();
      toast.success(`Call flow ${flow.active ? "deactivated" : "activated"}`);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to toggle call flow"));
    }
  };

  const handleToggle = async (flow: CallFlow) => {
    if (conflictingFlowFor(flow)) {
      setFlowToActivate(flow);
      return;
    }
    await applyToggle(flow);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading call flows...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-destructive">Failed to load call flows</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-muted-foreground text-sm">Manage how incoming calls are routed to your team</p>
        <Button onClick={() => navigate("/manage-organisation/call-flows/create")}>
          <Plus className="mr-2 h-4 w-4" />
          Create Flow
        </Button>
      </div>

      {!callFlows || callFlows.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Phone className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No call flows yet</h3>
            <p className="text-muted-foreground text-center mb-4 max-w-md">
              Create your first call flow to route incoming calls to your team members, voicemail, or external numbers.
            </p>
            <Button onClick={() => navigate("/manage-organisation/call-flows/create")}>
              <Plus className="mr-2 h-4 w-4" />
              Create Your First Flow
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Your Call Flows</CardTitle>
            <CardDescription>
              {callFlows.length} {callFlows.length === 1 ? "flow" : "flows"} configured
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>Actions</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Options</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {callFlows.map((flow) => (
                  <TableRow key={flow.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{flow.name}</div>
                        {flow.description && <div className="text-sm text-muted-foreground">{flow.description}</div>}
                      </div>
                    </TableCell>
                    <TableCell>
                      {flow.phoneNumber ? (
                        <div className="flex items-center gap-2">
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex">
                                  <Phone className="h-4 w-4 text-muted-foreground" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                {flow.phoneNumber.number || flow.phoneNumber.carrierPhoneNumberSid}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          {flow.phoneNumber.alias || flow.phoneNumber.carrierPhoneNumberSid}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">Not assigned</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-muted-foreground">
                        {flow.actions.length} {flow.actions.length === 1 ? "action" : "actions"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch checked={flow.active} onCheckedChange={() => handleToggle(flow)} />
                        <Badge variant={flow.active ? "default" : "secondary"}>
                          {flow.active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/manage-organisation/call-flows/${flow.id}/edit`)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggle(flow)}>
                            <Power className="mr-2 h-4 w-4" />
                            {flow.active ? "Deactivate" : "Activate"}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(flow)}>
                            <Copy className="mr-2 h-4 w-4" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setFlowToDelete(flow)} className="text-destructive">
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!flowToDelete} onOpenChange={() => setFlowToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Call Flow</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{flowToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ActiveFlowConflictDialog
        open={!!flowToActivate}
        onOpenChange={() => setFlowToActivate(null)}
        conflictingFlowName={(flowToActivate && conflictingFlowFor(flowToActivate)?.name) ?? "another flow"}
        phoneNumberLabel={
          flowToActivate?.phoneNumber?.alias || flowToActivate?.phoneNumber?.number || "This number"
        }
        onReplace={() => {
          const flow = flowToActivate;
          setFlowToActivate(null);
          if (flow) {
            applyToggle(flow);
          }
        }}
      />
    </div>
  );
}
